import { FC } from 'react';
import { RouterProvider } from 'react-router';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, useParams } from 'react-router-dom';

import { CustomerPortalWrapper, GateAppWrapper } from 'components/wrappers';
import { HistoryProvider, ProviderWrapper, RequireAuth, RequireRole, SessionWatcher } from 'contexts';
import { ResponseProvider } from 'contexts/response';
import { ErrorElement } from 'error-boundary';
import { 
  AccountTransfer,
  AllCarriers,
  BookingList,
  BulkUpdate,
  Cognito,
  CompletedTransactions,
  ContractDetails,
  Contracts,
  CreateLocation,
  CustomerRules,
  CustomersTab,
  CustomReports,
  Dashboard,
  EditBooking,
  EditCarrier,
  EditContract,
  EditCustomerRule,
  EditLocation,
  EditLocationRule,
  EditTransaction,
  EditUnit,
  EditUser,
  FleetFileConfiguration,
  GateAccess,
  GateUnits,
  InterchangeLookup,
  InvoiceDetailsOverview,
  Landing,
  LocationRules,
  LocationsTab,
  Login,
  Logout,
  MigrationPage,
  NewBooking,
  NewCarrier,
  NewCustomerMapping,
  NewCustomerRule,
  NewLocationRule,
  NewTransaction,
  NewUnitOrder,
  NewUser,
  NotFound,
  RedeliveryRoutes,
  Redirecting,
  RenumberUnit,
  ReviewTransaction,
  ScacManagementTabs,
  SettingsTabs,
  StorageInvoices,
  Templates,
  TransactionDrafts,
  TransactionList,
  Unauthorized,
  UnitDetails,
  UnitList,
  UnitOrders,
  UsersTab,
  ViewBooking,
  ViewCarrier,
  ViewCustomer,
  ViewLocation,
  ViewReport,
  ViewTransaction,
  ViewUser
} from 'pages';
import BulkContractMigrationPage from 'pages/settings/contract-migration/bulk-contract-migration-page';
import { EditCustomer } from 'pages/admin/customers/edit/edit-customer';
import EditEdiRecipients from 'pages/admin/customers/edit/edit-edi';
import EditCustomerFleetFiles from 'pages/admin/customers/edit/edit-fleet-files';
import { NewCustomer } from 'pages/admin/customers/new/new-customer';
import { DamageOptionsTab } from 'pages/admin/damage-options/damage-options-tab';
import { UnitOptionsTab } from 'pages/admin/unit-options/unit-options-tab';
import { SizeTypesTab } from 'pages/admin/size-types/size-types-tab';
import { EditReportGroup } from 'pages/admin/reports/edit-report-group';
import { NewReportGroup } from 'pages/admin/reports/new-report-group';
import { ReportsTab } from 'pages/admin/reports/reports-tab';
import EdiDashboard from 'pages/edi-dashboard/edi-dashboard';
import { Role } from 'types';

const BookingRoutes = (
  <Route
    path="bookings"
    element={
      <RequireRole
        allowedRoles={[
          Role.ADMIN,
          Role.BILLING_ADMIN,
          Role.BOOKING,
          Role.GATE_BOOKING,
          Role.SENIOR_BOOKING,
          Role.ADMIN_CUSTOMER,
          Role.CUSTOMER,
          Role.EXTERNAL
        ]}
      />
    }
  >
    <Route index element={<BookingList />} />
    <Route path="new">
      <Route index element={<NewBooking />} />
      {/* templates are denied to GATE_BOOKING by the API */}
      <Route
        element={
          <RequireRole
            allowedRoles={[
              Role.ADMIN,
              Role.BILLING_ADMIN,
              Role.BOOKING,
              Role.SENIOR_BOOKING,
              Role.ADMIN_CUSTOMER,
              Role.CUSTOMER,
              Role.EXTERNAL
            ]}
          />
        }
      >
        <Route path=":templateName" element={<NewBooking />} />
      </Route>
    </Route>
    <Route path=":bookingId">
      <Route index element={<ViewBooking />} />
      <Route
        path="edit"
        element={
          <RequireRole
            allowedRoles={[
              Role.ADMIN,
              Role.BILLING_ADMIN,
              Role.BOOKING,
              Role.GATE_BOOKING,
              Role.SENIOR_BOOKING,
              Role.ADMIN_CUSTOMER,
              Role.CUSTOMER,
              Role.EXTERNAL
            ]}
          />
        }
      >
        <Route index element={<EditBooking />} />
      </Route>
    </Route>
  </Route>
);

/* Admin only */
const ContractRoutes = (
  <Route path="contracts" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
    <Route index element={<Contracts />} />
    <Route path="new" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
      <Route index element={<EditContract />} />
    </Route>
    <Route path=":contractId">
      <Route index element={<ContractDetails />} />
      <Route path="edit" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<EditContract />} />
      </Route>
    </Route>
  </Route>
);

const TransactionRoutes = (
  <Route
    path="transactions"
    element={
      <RequireRole
        allowedRoles={[
          Role.ADMIN,
          Role.BILLING_ADMIN,
          Role.BOOKING,
          Role.GATE_BOOKING,
          Role.SENIOR_BOOKING,
          Role.CARRIER,
          Role.ADMIN_CUSTOMER,
          Role.CUSTOMER,
          Role.EXTERNAL
        ]}
      />
    }
  >
    <Route index element={<TransactionList />} />
    <Route path=":transactionId">
      <Route index element={<ViewTransaction />} />
      <Route
        path="edit"
        element={
          <RequireRole
            allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.GATE_BOOKING, Role.SENIOR_BOOKING]}
          />
        }
      >
        <Route index element={<EditTransaction />} />
      </Route>
    </Route>
  </Route>
);

const YardManagementRoutes = (
  <>
    <Route
      path="units"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.GATE_BOOKING,
            Role.SENIOR_BOOKING,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ]}
        />
      }
    >
      <Route index element={<UnitList />} />
      <Route path="new" />
      <Route path=":unitId">
        <Route index element={<UnitDetails />} />
        <Route
          path="edit"
          element={
            <RequireRole
              allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.GATE_BOOKING, Role.SENIOR_BOOKING]}
            />
          }
        >
          <Route index element={<EditUnit />} />
        </Route>
        <Route
          path="transfer"
          element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING]} />}
        >
          <Route index element={<RenumberUnit />} />
        </Route>
      </Route>
      <Route
        path="bulk"
        element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING]} />}
      >
        <Route index element={<BulkUpdate />} />
        <Route
          path="transfer"
          element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING]} />}
        >
          <Route index element={<AccountTransfer />} />
        </Route>
      </Route>
    </Route>
    <Route
      path="orders"
      element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING]} />}
    >
      <Route index element={<UnitOrders />} />
      <Route path="new" element={<NewUnitOrder />} />
    </Route>
  </>
);

const RedirectCarrierDetail: FC = () => {
  const { id } = useParams();
  return <Navigate to={`/scac-management/carriers/${id}`} replace />;
};

const RedirectCarrierEdit: FC = () => {
  const { id } = useParams();
  return <Navigate to={`/scac-management/carriers/${id}/edit`} replace />;
};

const SettingsRoutes = (
  <Route
    path="settings"
    element={
      <RequireRole
        allowedRoles={[
          Role.ADMIN,
          Role.BILLING_ADMIN,
          Role.BOOKING,
          Role.SENIOR_BOOKING,
          Role.CARRIER,
          Role.ADMIN_CUSTOMER,
          Role.CUSTOMER,
          Role.EXTERNAL
        ]}
      />
    }
  >
    <Route element={<SettingsTabs />}>
      <Route index element={<ViewUser />} />
      <Route path="users" element={<RequireRole allowSelf allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<UsersTab />} />
        <Route path=":username">
          <Route index element={<ViewUser />} />
          <Route path="edit" element={<EditUser />} />
        </Route>
        <Route path="new">
          <Route index element={<NewUser />} />
        </Route>
      </Route>
      <Route path="carriers">
        <Route index element={<Navigate to="/scac-management" replace />} />
        <Route path="new" element={<Navigate to="/scac-management/carriers/new" replace />} />
        <Route path=":id">
          <Route index element={<RedirectCarrierDetail />} />
          <Route path="edit" element={<RedirectCarrierEdit />} />
        </Route>
      </Route>
      <Route
        path="customers"
        element={
          <RequireRole
            allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL]}
          />
        }
      >
        <Route index element={<CustomersTab />} />
        <Route path=":id" element={<RequireRole allowCustomer allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
          <Route index element={<ViewCustomer />} />
          <Route path="edit" element={<EditCustomer />} />
          <Route path="edi-recipients" element={<EditEdiRecipients />} />
          <Route path="fleet-files" element={<EditCustomerFleetFiles />} />
        </Route>
        <Route path="new" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
          <Route index element={<NewCustomer />} />
        </Route>
      </Route>
      <Route
        path="locations"
        element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING]} />}
      >
        <Route index element={<LocationsTab />} />
        <Route path=":id">
          <Route index element={<ViewLocation />} />
          <Route path="edit" element={<EditLocation />} />
        </Route>
        <Route path="new" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
          <Route index element={<CreateLocation />} />
        </Route>
      </Route>
      <Route path="damage-options" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<DamageOptionsTab />} />
      </Route>
      <Route path="unit-options" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<UnitOptionsTab />} />
      </Route>
      <Route path="size-types" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<SizeTypesTab />} />
      </Route>
      <Route path="report-groups" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<ReportsTab />} />
        <Route path="new" element={<NewReportGroup />} />
        <Route path=":id" element={<RequireRole allowCustomer allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
          <Route index element={<ViewReport />} />
          <Route path="edit" element={<EditReportGroup />} />
        </Route>
      </Route>
      <Route path="fleet-files" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<FleetFileConfiguration />} />
        <Route path="new" element={<NewCustomerMapping />} />
      </Route>
      <Route path="edi-dashboard" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<EdiDashboard />} />
      </Route>
      <Route path="migration" element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}>
        <Route index element={<MigrationPage />} />
      </Route>
      <Route
        path="contract-migration"
        element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}
      >
        <Route index element={<BulkContractMigrationPage />} />
      </Route>
    </Route>
  </Route>
);

const CustomerPortalRoutes = (
  <Route path="/" element={<CustomerPortalWrapper />}>
    {BookingRoutes}
    {ContractRoutes}
    <Route
      path="dashboard"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.CARRIER,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ]}
        />
      }
    >
      <Route index element={<Dashboard />} />
    </Route>
    <Route
      path="invoices"
      element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING]} />}
    >
      <Route index element={<StorageInvoices />} />
      <Route path=":invoiceId" element={<InvoiceDetailsOverview />} />
    </Route>
    {RedeliveryRoutes}
    <Route
      path="scac-management"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ]}
        />
      }
    >
      <Route element={<ScacManagementTabs />}>
        <Route
          element={
            <RequireRole
              allowedRoles={[
                Role.ADMIN,
                Role.BILLING_ADMIN,
                Role.BOOKING,
                Role.SENIOR_BOOKING,
                Role.ADMIN_CUSTOMER
              ]}
            />
          }
        >
          <Route index element={<AllCarriers />} />
          <Route path="carriers">
            <Route
              element={
                <RequireRole
                  allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.ADMIN_CUSTOMER]}
                />
              }
            >
              <Route path="new" element={<NewCarrier />} />
              <Route path=":id/edit" element={<EditCarrier />} />
            </Route>
            <Route path=":id">
              <Route index element={<ViewCarrier />} />
            </Route>
          </Route>
        </Route>
        <Route
          path="account-rules"
          element={
            <RequireRole
              allowedRoles={[
                Role.ADMIN,
                Role.BILLING_ADMIN,
                Role.ADMIN_CUSTOMER,
                Role.BOOKING,
                Role.CUSTOMER,
                Role.EXTERNAL,
                Role.SENIOR_BOOKING
              ]}
            />
          }
        >
          <Route index element={<CustomerRules />} />
          <Route path=":id" element={<EditCustomerRule />} />
          <Route path="new" element={<NewCustomerRule />} />
        </Route>
        <Route
          path="location-rules"
          element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING, Role.SENIOR_BOOKING]} />}
        >
          <Route index element={<LocationRules />} />
          <Route path=":id" element={<EditLocationRule />} />
          <Route path="new" element={<NewLocationRule />} />
        </Route>
      </Route>
    </Route>
    {SettingsRoutes}
    <Route
      path="templates"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ]}
        />
      }
    >
      <Route index element={<Templates />} />
      <Route path=":templateName" element={<NewBooking />} />
    </Route>
    <Route
      path="reports"
      element={<RequireRole allowedRoles={[Role.ADMIN, Role.BILLING_ADMIN]} />}
    >
      <Route index element={<CustomReports />} />
    </Route>
    {TransactionRoutes}
    {YardManagementRoutes}
  </Route>
);

const GateAppRoutes = (
  <Route path="gate" element={<GateAppWrapper />}>
    <Route
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<GateAccess />} />
    </Route>
    <Route
      path="complete"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<CompletedTransactions />} />
    </Route>
    <Route
      path="new"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<NewTransaction />} />
    </Route>
    <Route
      path="pending"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<TransactionDrafts />} />
    </Route>
    <Route
      path="review"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<ReviewTransaction />} />
    </Route>
    <Route
      path="units"
      element={
        <RequireRole
          allowedRoles={[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.BOOKING,
            Role.SENIOR_BOOKING,
            Role.GATE,
            Role.GATE_BOOKING
          ]}
        />
      }
    >
      <Route index element={<GateUnits />} />
    </Route>
  </Route>
);

const dataRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route index element={<Navigate to="login" />} />
      <Route path="cognito" element={<Cognito />} />
      <Route path="landing" element={<Landing />} />
      <Route path="login" element={<Login />} />
      <Route path="logout" element={<Logout />} />
      <Route path="unauthorized" element={<Unauthorized />} />
      <Route path="interchange-lookup" element={<ResponseProvider><InterchangeLookup /></ResponseProvider>} />
      <Route path="/" element={<RequireAuth />}>
        <Route element={<SessionWatcher />}>
          <Route element={<HistoryProvider />}>
            <Route element={<ProviderWrapper />} errorElement={<ErrorElement />}>
              <Route path="redirect" element={<Redirecting />} />
              {CustomerPortalRoutes}
              {GateAppRoutes}
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </>
  )
);

const Router: FC = () => <RouterProvider router={dataRouter} />;

export default Router;
